import { useState } from 'react';
import { useInAppBrowser } from '@/hooks/useInAppBrowser';

export default function InAppBrowserWarning() {
  const { isInAppBrowser, appName } = useInAppBrowser();
  const [copied, setCopied] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  if (!isInAppBrowser || dismissed) return null;

  const currentUrl = window.location.href;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(currentUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch {
      // fallback for older browsers
      const el = document.createElement('input');
      el.value = currentUrl;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    }
  };

  const appIcon =
    appName === 'TikTok' ? 'ri-tiktok-line' :
    appName === 'Instagram' ? 'ri-instagram-line' :
    appName === 'Facebook' ? 'ri-facebook-circle-line' :
    appName === 'Snapchat' ? 'ri-snapchat-line' :
    'ri-smartphone-line';

  return (
    <div className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-black/70 backdrop-blur-sm px-6" dir="rtl">
      <div className="bg-white rounded-3xl p-8 max-w-sm w-full text-center shadow-2xl">

        {/* Icon */}
        <div className="w-20 h-20 mx-auto mb-5 rounded-2xl bg-gradient-to-br from-[#C9A961]/20 to-[#C9A961]/5 flex items-center justify-center border-2 border-[#C9A961]/20">
          <i className={`${appIcon} text-4xl text-[#C9A961]`} />
        </div>

        {/* Title */}
        <h2 className="text-xl font-black text-gray-900 mb-2">
          افتح الرابط في متصفحك
        </h2>

        {/* Description */}
        <p className="text-gray-500 text-sm leading-relaxed mb-1">
          يبدو أنك تصفح داخل تطبيق <strong className="text-gray-700">{appName}</strong>
        </p>
        <p className="text-gray-500 text-sm leading-relaxed mb-6">
          متصفح {appName} الداخلي لا يدعم صفحات الدفع. انسخ الرابط وافتحه في <strong className="text-gray-700">Safari</strong> أو <strong className="text-gray-700">Chrome</strong> للدفع بأمان.
        </p>

        {/* Steps */}
        <div className="bg-[#FAFAF8] rounded-2xl p-4 mb-5 text-right space-y-3">
          {[
            { num: '١', text: 'انسخ الرابط بالزر أدناه' },
            { num: '٢', text: 'افتح متصفح Safari أو Chrome' },
            { num: '٣', text: 'الصق الرابط في شريط العنوان' },
          ].map(step => (
            <div key={step.num} className="flex items-center gap-3">
              <div className="w-7 h-7 flex-shrink-0 flex items-center justify-center bg-[#C9A961] rounded-full">
                <span className="text-white text-xs font-black">{step.num}</span>
              </div>
              <span className="text-gray-700 text-sm font-medium">{step.text}</span>
            </div>
          ))}
        </div>

        {/* URL box */}
        <div className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 mb-4 text-left overflow-hidden">
          <p className="text-gray-400 text-xs truncate">{currentUrl}</p>
        </div>

        {/* Copy button */}
        <button
          onClick={handleCopy}
          className={`w-full flex items-center justify-center gap-2 py-4 rounded-2xl font-black text-white text-base cursor-pointer whitespace-nowrap transition-all duration-300 mb-3 ${
            copied ? 'bg-green-500' : 'bg-[#C9A961] hover:bg-[#A07C30]'
          }`}
        >
          <i className={copied ? 'ri-check-line' : 'ri-links-line'} />
          {copied ? 'تم النسخ! افتح متصفحك والصق الرابط' : 'نسخ الرابط'}
        </button>

        {/* Dismiss */}
        <button
          onClick={() => setDismissed(true)}
          className="text-gray-400 text-sm underline cursor-pointer hover:text-gray-600 transition-colors"
        >
          تجاهل والاستمرار (قد لا يعمل الدفع)
        </button>
      </div>
    </div>
  );
}
